import React, { useEffect, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Drawer,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Modal,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { withTranslation } from 'react-i18next';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DateTime } from 'luxon';

// Import your custom components and utilities
import ToastMsg from '../../../../Components/ToastMsg/ToastMsg';
import { ConfirmPopup } from '../../../../Components/Common Components/CommonComponents';
import { AmountsCard } from '../../../../Components/CommonCards/CommonCards';
import { CheckAccessFunc, formatDate } from '../../../../Components/CommonFunctions/CommonFunctions';
import { localGetItem } from '../../../../Utility/Services/CacheProviderService';
import { ImagePaths } from '../../../../Utility/ImagePaths';
import CommonValidation from '../../../../Components/CommonFunctions/CommonValidation';
import CommonDatePicker from '../../../../Components/Common Components/CommonDatePicker';
import { Serviceurls } from '../../../../Utility/API/Serviceurls';
import RestAPIService from '../../../../Utility/Services/RestAPIService';

function TelemedicineConfig({ t }) {
  // Consolidated state management
  const [state, setState] = useState({
    // Main data states
    timeUsed: '',
    timeRequestData: [],

    // UI control states
    timeReqPopup: false,
    filterOpen: false,
    isLoader: false,
    isErrorMsg: false,
    isErrorMsgText: '',
    successMsg: false,
    successMsgText: '',
    disableBtn: false,
    isEdit: false,
    deleteRequest: false,
    datePickerOpen: false,

    // Form states
    requestNo: '',
    requestDate: new Date(),
    requestBy: '',
    userName: '',
    timeMin: '',
    requestNotes: '',

    // Filter states
    fromDate: DateTime.fromJSDate(new Date()).toFormat('yyyy-MM-dd'),
    toDate: DateTime.fromJSDate(new Date()).toFormat('yyyy-MM-dd'),
    dateIndex: 1,
    filterReqNo: '',
    filterRequestBy: [],
    filterStatus: '',
    filterType: '',

    // Dropdown data
    requestByData: [],
    filterStatusData: ["Pending", "Completed", "Cancelled"]
  });

  // Helper function to update state
  const updateState = (updates) => {
    setState(prevState => ({
      ...prevState,
      ...updates
    }));
  };

  useEffect(() => {
    const userName = localGetItem("LogedUserName");
    updateState({ userName });
    getTelemedicineData();
    GetUserDetails();
  }, []);

  const getTelemedicineData = () => {
    try {
      updateState({ isLoader: true });
      RestAPIService.getAll(Serviceurls.TELE_REQUEST + `?from_date=${state.fromDate}&to_date=${state.toDate}&request_no=${state.filterReqNo || ''}&request_by=${state.filterRequestBy.map(i => i.user_name).join(',')}&status=${state.filterStatus || ''}`)
        .then((response) => {
          if (response.data.status === 'success') {
            const data = response.data.data || [];
            const timeUsed = `${response?.data?.used_duration} / ${response?.data?.total_duration}`;
            updateState({ timeRequestData: data, timeUsed: timeUsed, isLoader: false });
          } else {
            updateState({ isLoader: false });
            errorMessage(response.data.message || 'Failed to fetch data');
          }
        })
        .catch((e) => {
          updateState({ isLoader: false });
          errorMessage(e.response?.data?.message || e.message);
        });
    } catch (e) {
      updateState({ isLoader: false });
      errorMessage(e.message);
    }
  }

  const printRequest = () => {
    try {
      updateState({ disableBtn: true })
      RestAPIService.getAll(Serviceurls.TELE_REQUEST + `?from_date=${state.fromDate}&to_date=${state.toDate}&request_no=${state.filterReqNo || ''}&request_by=${state.filterRequestBy.map(i => i.user_name).join(',')}&status=${state.filterStatus || ''}&export_type=pdf`)
        .then((response) => {
          const file = new Blob(
            [response.data],
            { type: 'application/pdf' }
          )
          const fileURL = URL.createObjectURL(file)
          window.open(fileURL)
          updateState({ disableBtn: false })
        }).catch((error) => {
          errorMessage(error.message)
          updateState({ disableBtn: false })
        })
    } catch (e) {
      errorMessage(e?.message)
      updateState({ disableBtn: false })
    }
  }

  const exportRequest = () => {
    try {
      updateState({ disableBtn: true })
      RestAPIService.excelGet(`${Serviceurls.TELE_REQUEST}?from_date=${state.fromDate}&to_date=${state.toDate}&request_no=${state.filterReqNo || ''}&request_by=${state.filterRequestBy.map(i => i.user_name).join(',')}&status=${state.filterStatus || ''}&export_type=excel`).
        then((response) => {
          if (response.data) {
            var pom = document.createElement('a');
            var csvContent = response.data; //here we load our csv data 

            let filename = response?.headers["content-disposition"]?.split("filename=")[1]
            // var blob = new Blob([csvContent], {type: 'data:application/vnd.ms-excel;base64'});

            var blob = new Blob([csvContent], { type: 'application/x-www-form-urlencoded; charset=UTF-8' }); // text/csv;charset=utf-8;
            var url = URL.createObjectURL(blob);
            pom.href = url;
            pom.setAttribute('download', filename ? filename : 'registry.xlsx');
            pom.click();
           updateState({ disableBtn: false })
          }
        }).catch((error) => {
          if (error.response?.data?.message) {
            errorMessage(error.response?.data?.message);
          } else {
            errorMessage(error.message);
          }
          updateState({ disableBtn: false })
        })
    } catch (e) {
      errorMessage(e?.message)
      updateState({ disableBtn: false })
    }
  }

  const GetUserDetails = () => {
    try {
      RestAPIService.getAll(Serviceurls.DAY_END_USER_DETAIL_GET + `?show_all=true`)
        .then((response) => {
          if (response.data.status === 'success') {
            updateState({ requestByData: response.data.data || [] });
          }
        }).catch((error) => {
          if (error?.response?.data?.status === 'fail') {
            errorMessage(error.response.data.message)
          } else {
            errorMessage(error.message)
          }

        })
    } catch (error) {
      errorMessage(error)
    }
  }

  const clearData = () => {
    updateState({
      timeReqPopup: false,
      isEdit: false,
      requestNo: '',
      requestDate: new Date(),
      requestBy: '',
      timeMin: '',
      requestNotes: ''
    });
  };
  const errorMessage = (msg) => {
    updateState({ isErrorMsg: true, isErrorMsgText: msg });
  }
  const successMessage = (msg) => {
    updateState({ successMsg: true, successMsgText: msg });
  }
  const sendRequest = () => {
    try {
      if (state.timeMin === '' || state.timeMin === '0') {
        updateState({ disableBtn: false });
        errorMessage('Please enter valid Time (Minutes)');
        return;
      }
      let reqDate = state.requestDate ? DateTime.fromJSDate(state.requestDate).toFormat('yyyy-MM-dd HH:mm:ss') : null
      const data = {
        'request_date': reqDate,
        'request_by': state.isEdit ? state.requestBy : (state.userName || ''),
        'duration_count': (+state.timeMin) || null,
        'notes': state.requestNotes || '',
      }
      if (state.isEdit) {
        data.id = state.editID;
      }
      RestAPIService.create(data, Serviceurls.TELE_REQUEST)
        .then((response) => {
          if (response.data.status === 'success') {
            successMessage(response.data.message || 'Request sent successfully');
            getTelemedicineData();
            clearData();
            updateState({ disableBtn: false });
          }
        })
        .catch((e) => {
          updateState({ disableBtn: false });
          errorMessage(e.response.data.message || e.message);
        }
        );
    } catch (e) {
      updateState({ disableBtn: false });
      errorMessage(e.message);
    }
  }
  const renderRaisePopup = () => {
    return (
      <Modal open={state.timeReqPopup} onClose={clearData}>
        <Box className="eMed_ImgPreviewPop_Main" height={'50vh'}>
          <Box component={'div'} className="eMed_ImgPreviewPop_Header">
            <Typography fontWeight={'bold'} variant='h6'>
              {state.isEdit ? `Telemedicine Request - Request No: ${state.requestNo}` : "Raise Telemedicine Request"}
            </Typography>
            <Button emed_tid='req_popup_close_testID' onClick={clearData}>
              <img src={ImagePaths.LabCloseButton.default} alt="close" className='eMed_action_img_small' />
            </Button>
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '1vw', alignContent: 'center', margin: '1vw' }}>
            <Box sx={{ margin: '1vw' }}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  open={state.datePickerOpen}
                  onOpen={() => updateState({ datePickerOpen: true })}
                  onClose={() => updateState({ datePickerOpen: false })}
                  value={state.requestDate}
                  disabled={true}
                  name={"datePicker"}
                  inputFormat='DD-MM-YYYY'
                  views={["year", "month", "day"]}
                  renderInput={(params) => (
                    <TextField
                      onKeyDown={(e) => e.preventDefault()}
                      variant='standard'
                      label='Request Date *'
                      size='small'
                      sx={{ width: '25vw', mr: '1.5vw', paddingTop: '1vw' }}
                      {...params}
                      onClick={() => updateState({ datePickerOpen: true })}
                      inputProps={{ ...params.inputProps, 'emed_tid': 'req_popup_datepicker_testID' }}
                    />
                  )}
                />
              </LocalizationProvider>
              <TextField
                label='Request By *'
                size='small'
                autoComplete='off'
                variant='standard'
                sx={{ width: '25vw' }}
                inputProps={{ emed_tid: "req_popup_requestBy_testID", maxLength: 30 }}
                disabled={true}
                value={state.isEdit ? state.requestBy : state.userName}
              />
            </Box>
            <Box sx={{ margin: '1vw' }}>
              <TextField
                label='Time (Minutes) *'
                variant='standard'
                size='small'
                autoComplete='off'
                sx={{ width: '25vw', mr: '1.5vw' }}
                inputProps={{ emed_tid: "req_popup_timeMin_testID", maxLength: 30 }}
                value={state.timeMin}
                onChange={(e) => {
                  if ((CommonValidation.numberOnly(e.target.value) || e.target.value === '') && e.target.value.length <= 7) {
                    updateState({ timeMin: e.target.value });
                  }
                }}
              />
              <TextField
                label='Notes'
                variant='standard'
                size='small'
                autoComplete='off'
                sx={{ width: '25vw' }}
                value={state.requestNotes}
                inputProps={{ emed_tid: "req_popup_notes_testID", maxLength: 250 }}
                onChange={(e) => {updateState({ requestNotes: e.target.value })}}
              />
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: '1vw', m: '2vw', justifyContent: 'flex-end' }}>
            <Button variant='outlined' sx={{ textTransform: 'capitalize' }} emed_tid="req_popup_cancel_testID" onClick={clearData}>
              Cancel
            </Button>
            <Button
              variant='contained'
              emed_tid="req_popup_sendReq_testID"
              sx={{ textTransform: 'capitalize' }}
              disabled={state.disableBtn}
              onClick={() => {
                updateState({ disableBtn: true });
                sendRequest();
              }}
            >
              {state.isEdit ? "Edit Request" : "Send Request"}
            </Button>
          </Box>
        </Box>
      </Modal >
    );
  };

  const handleFilterDate = (from, to, index) => {
    updateState({
      fromDate: from,
      toDate: to,
      dateIndex: index
    });
  };

  const renderFilterScreen = () => {
    return (
      <Box component={"div"} className='eMed_rts_filter_container'>
        <Box component={"div"} className='eMed_filter_Top'>
          <Typography variant='h6' fontWeight={600}>{t('Filter')}</Typography>
          <Button onClick={() => updateState({ filterOpen: false })} className='eMed_usrconf_btn' emed_tid="filter_close_testID">
            <img src={ImagePaths.LabCloseButton.default} alt="close" className='eMed_action_img_small' />
          </Button>
        </Box>
        <Box className='eMed_rts_middle'>
          <Box className='eMed_date_filter'>
            <CommonDatePicker
              FromDate={state.fromDate}
              ToDate={state.toDate}
              DateIndex={state.dateIndex}
              SelectedDates={handleFilterDate}
              HideAllMenu={true}
            />
          </Box>
          <Box className='eMed_rts_dropdown'>
            <TextField
              label='Request No'
              size='small'
              autoComplete='off'
              sx={{ width: '20vw' }}
              inputProps={{ emed_tid: "filter_reqNo_testID", maxLength: 30 }}
              value={state.filterReqNo}
              onChange={(e) => {
                if (CommonValidation.ALPHA_NUMARIC(e.target.value) || e.target.value === '') {
                  updateState({ filterReqNo: e.target.value });
                }
              }}
            />
          </Box>
          <Box className='eMed_rts_dropdown'>
            <Autocomplete
              size='small'
              multiple
              autoComplete='off'
              limitTags={1}
              options={state.requestByData}
              getOptionLabel={(option) => option?.user_name}
              value={state.filterRequestBy}
              onChange={(event, newValue) => updateState({ filterRequestBy: newValue })}
              renderInput={(params) => (
                <TextField
                  {...params}
                  inputProps={{ ...params.inputProps, 'emed_tid': 'filter_requestBy_testID' }}
                  label={t("Request By")}
                />
              )}
            />
          </Box>
          <Box className='eMed_rts_dropdown'>
            <Autocomplete
              size='small'
              options={state.filterStatusData}
              getOptionLabel={(option) => option}
              value={state.filterStatus}
              onChange={(event, newValue) => updateState({ filterStatus: newValue })}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t("Status")}
                  inputProps={{ ...params.inputProps, 'emed_tid': 'filter_status_testID' }}
                />
              )}
            />
          </Box>
        </Box>
        <Stack spacing={2} direction="row" className='eMed_rts_btn' alignItems="center" justifyContent="center">
          <Button
            variant='outlined'
            size="small"
            emed_tid="filter_clear_testID"
            onClick={() => {
              updateState({
                fromDate: DateTime.fromJSDate(new Date()).toFormat('yyyy-MM-dd'),
                toDate: DateTime.fromJSDate(new Date()).toFormat('yyyy-MM-dd'),
                dateIndex: 1,
                filterReqNo: '',
                filterRequestBy: [],
                filterStatus: '',
                filterType: '',
              });
            }}
            id="eMed_Config_btn"
          >
            {t("Clear")}
          </Button>
          <Button
            variant='contained'
            size="small"
            emed_tid="filter_search_testID"
            onClick={() => { updateState({ filterOpen: false }); getTelemedicineData(); }}
            id="eMed_Config_btn"
          >
            {t("Search")}
          </Button>
        </Stack>
      </Box>
    );
  };

  const confirmRemove = () => {
    updateState({ deleteRequest: false });
    try {
      let reqDate = state.requestDate ? DateTime.fromJSDate(state.requestDate).toFormat('yyyy-MM-dd HH:mm:ss') : null
      const data = {
        'request_date': reqDate,
        'request_by': state.isEdit ? state.requestBy : (state.userName || ''),
        'duration_count': (+state.timeMin) || null,
        'notes': state.requestNotes || '',
        'id': state.editID,
        'status': 'Cancelled'
      }
      RestAPIService.create(data, Serviceurls.TELE_REQUEST)
        .then((response) => {
          if (response.data.status === 'success') {
            successMessage(response.data.message || 'Request sent successfully');
            getTelemedicineData();
            clearData();
            updateState({ disableBtn: false });
          }
        })
        .catch((e) => {
          updateState({ disableBtn: false });
          errorMessage(e.response.data.message || e.message);
        }
        );
    } catch (e) {
      updateState({ disableBtn: false });
      errorMessage(e.message);
    }
  };

  const popClose = () => {
    updateState({ deleteRequest: false, disableBtn: false });
  };

  const msgClose = () => {
    updateState({
      isErrorMsg: false,
      isErrorMsgText: '',
      successMsg: false,
      successMsgText: '',
    });
  };

  const closeFilter = () => {
    updateState({ filterOpen: false });
  };

  state.timeRequestData?.forEach((element, index) => element.sno = index + 1)
  const columns = [
    {
      field: "sno",
      headerName: t("S No"),
      flex: 0.01,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => (<Box component={'div'}>{params?.row?.sno ? params?.row?.sno : "-"}</Box>)
    },
    {
      field: "request_no",
      headerName: t("Request No"),
      flex: 0.142,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => (
        <Box component={'div'}>
          {params?.row?.request_no?.length > 15 ?
            <Tooltip placement='top' title={params?.row?.request_no} arrow>
              <div>{params?.row?.request_no?.slice(0, 12) + "..."}</div>
            </Tooltip>
            : params?.row?.request_no ? params?.row?.request_no : "-"
          }
        </Box>
      )
    },
    {
      field: "request_date",
      headerName: t("Request Date"),
      flex: 0.142,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => (
        <Box component={'div'}>{params?.row?.request_date ? formatDate(params?.row?.request_date) : '-'}</Box>
      )
    },
    {
      field: "request_by",
      headerName: t("Request by"),
      flex: 0.142,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => (
        <Box component={'div'}>
          {params?.row?.request_by?.length > 15 ?
            <Tooltip placement='top' title={params?.row?.request_by} arrow>
              <div>{params?.row?.request_by?.slice(0, 12) + "..."}</div>
            </Tooltip>
            : params?.row?.request_by ? params?.row?.request_by : "-"
          }
        </Box>
      )
    },
    {
      field: "duration_count",
      headerName: t("Time (Minutes)"),
      flex: 0.1,
      headerAlign: "center",
      align: "center",
    },
    {
      field: "notes",
      headerName: t("Notes"),
      flex: 0.142,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => (
        <Box component={'div'}>
          {params?.row?.notes?.length > 20 ?
            <Tooltip placement='top' title={params?.row?.notes} arrow>
              <div>{params?.row?.notes?.slice(0, 17) + "..."}</div>
            </Tooltip>
            : params?.row?.notes ? params?.row?.notes : "-"
          }
        </Box>
      )
    },
    {
      field: "status",
      flex: 0.142,
      headerName: t("Status"),
      headerAlign: "center",
      align: "center",
      renderCell: ({ row }) => {
        const divColor = row?.status === "Completed" ? "#ECFFE9" : row?.status === "Pending" ? "#FFE9E9" : null;
        const txtColor = row?.status === "Completed" ? "#1E972A" : row?.status === "Pending" ? "#B82121" : null;
        return (
          <Stack sx={{ padding: "0.4vw", borderRadius: "0.5vw", backgroundColor: divColor }}>
            <Typography sx={{ color: txtColor, fontSize: '0.9vw' }}>{row?.status ? row?.status : "-"}</Typography>
          </Stack>
        );
      }
    },
    {
      field: "action",
      flex: 0.10,
      headerName: t("Action"),
      headerAlign: "center",
      align: "center",
      renderCell: ({ row }) => {
        const allowEdit = row?.status === "Pending";
        const allowCancel = !(row?.status === "Cancelled" || row?.status === "Completed");
        const date = DateTime.fromFormat(row?.request_date, 'dd-MM-yyyy')?.toFormat('yyyy-MM-dd');

        return (
          <Box display={"flex"} flexDirection={"row"} alignItems={"center"}>
            {allowEdit && (
              <Tooltip title="Edit" placement="top" arrow>
                <div className="eMed_pause">
                  <IconButton
                    disabled={!CheckAccessFunc("front_office", "Settings", "Telemedicine Configuration", "Telemedicine Request", "SubTab")?.editAccess}
                    onClick={() => {
                      updateState({
                        isEdit: true,
                        timeReqPopup: true,
                        requestNo: row?.request_no || '',
                        requestBy: row?.request_by || '',
                        requestDate: new Date(date),
                        timeMin: row?.duration_count || '',
                        requestNotes: row?.notes || '',
                        editID: row?.id || null
                      });
                    }}
                  >
                    <img style={{ width: '1.5vw', height: '1.5vw' }} src={ImagePaths.LabEditButton.default} alt='edit' />
                  </IconButton>
                </div>
              </Tooltip>
            )}
            {allowCancel && (
              <Tooltip title={t("Remove")} placement="top" arrow>
                <IconButton
                  emed_tid="ord_rmv"
                  className='eMed_usrconf_btn'
                  disabled={!CheckAccessFunc("front_office", "Settings", "Telemedicine Configuration", "Telemedicine Request", "SubTab")?.editAccess}
                  onClick={() => {
                    updateState({
                      deleteRequest: true,
                      requestNo: row?.request_no || '',
                      requestBy: row?.request_by || '',
                      requestDate: new Date(date),
                      timeMin: row?.duration_count || '',
                      requestNotes: row?.notes || '',
                      editID: row?.id || null
                    });
                  }}
                >
                  <img src={ImagePaths.CrossIcon.default} alt="Remove" style={{ width: '1.5vw', height: '1.5vw' }} />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        );
      }
    },
  ];

  return (
    <Box component={'div'} className='eMed_rts_container'>
      <Box component={"div"} className='eMed_Top_Container'>
        <Box component={"div"} className='eMed_Cards_Container'>
          {AmountsCard(t("Time (Min) Used"), state.timeUsed || 0, true, "white", false, null, 'time_left_card')}
        </Box>
        <Box component={"div"} className='eMed_Action_Container' width={'40%'}>
          <Button
            emed_tid='drt_po_req'
            className='eMed_Filter_Btns'
            variant='contained'
            disabled={!CheckAccessFunc("front_office", "Settings", "Telemedicine Configuration", "Telemedicine Request", "SubTab")?.editAccess}
            onClick={() => updateState({ timeReqPopup: true })}
          >
            {`+ Raise Request`}
          </Button>
          <Box component={"div"} className='eMed_Filter_Icon_div'>
            <IconButton emed_tid="filter_btn_testID" onClick={() => updateState({ filterOpen: true })}>
              <img src={ImagePaths.Filter.default} alt="filter" className='eMed_action_img' />
            </IconButton>
          </Box>
          <IconButton emed_tid="print_btn_testID" disabled={state.disableBtn} onClick={() => { printRequest(); }}>
            <img src={ImagePaths.LabPrintIcon.default} alt="print" className='eMed_action_img' />
          </IconButton>
          <IconButton emed_tid="excel_btn_testID" disabled={state.disableBtn} onClick={() => { exportRequest(); }}>
            <img src={ImagePaths.LabUploadButton.default} alt="upload" className='eMed_action_img' />
          </IconButton>
        </Box>
      </Box>
      <Box component={'div'} className='eMed_rts_table'>
        <DataGrid
          rows={state.timeRequestData}
          columns={columns}
          getRowId={(row) => row['id']}
          loading={state.isLoader}
          hideFooterSelectedRowCount
          localeText={{
            toolbarColumns: "",
            toolbarDensity: "",
            toolbarExport: "",
            toolbarFilters: "",
            toolbarExportPrint: ""
          }}
          headerHeight={40}
          components={{
            NoRowsOverlay: () => (
              <Stack className='eMed_conf_nodata'>
                {t("NoRecordsFound")}
              </Stack>
            )
          }}
          rowsPerPageOptions={[10, 20, 30]}
          disableSelectionOnClick
        />
      </Box>
      {state.timeReqPopup && renderRaisePopup()}
      <Drawer
        anchor={'right'}
        open={state.filterOpen}
        ModalProps={{ onBackdropClick: closeFilter }}
      >
        {renderFilterScreen()}
      </Drawer>
      {state.isErrorMsg && (
        <ToastMsg
          severity={'error'}
          msg={state.isErrorMsgText}
          msgPop={msgClose.bind(this)}
        />
      )}
      {state.successMsg && (
        <ToastMsg
          severity={'success'}
          msg={state.successMsgText}
          msgPop={msgClose.bind(this)}
        />
      )}
      {state.deleteRequest && (
        <ConfirmPopup
          cnfTitle={t('Confirmation')}
          cnfContent={t('Are you sure, do you want to Cancel Telemedicine request ?')}
          cnfAlertPopupClose={popClose}
          cnfData={confirmRemove}
          cnfDisable={state.disableBtn}
        />
      )}
    </Box>
  );
}

export default withTranslation()(TelemedicineConfig);